import { ChangeDetectionStrategy, ChangeDetectorRef, Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MaterialModule } from 'src/app/shared/material/material.module';
import { LAYOUT_MENU_ITEMS } from './layout-menu.constants';
import { Router, RouterModule, EventType, NavigationEnd } from '@angular/router';
import { RequirePermissionDirective } from 'src/app/shared/directives/require-permission/require-permission.directive';
import { MenuItem, MenuItemType } from './layout-menu.models';
import { TemplatesDomainService } from 'src/app/modules/templates/services/templates-domain.service';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { OfflineDomainService } from 'src/app/shared/utils/offline/services/offline-domain.service';
import { DisabledDirective } from 'src/app/shared/directives/disabled.directive';
import {
  Subject,
  combineLatest,
  defer,
  distinctUntilChanged,
  filter,
  map,
  merge,
  shareReplay,
  startWith,
  tap,
} from 'rxjs';
import * as R from 'remeda';

@Component({
  selector: 'app-layout-menu',
  standalone: true,
  imports: [
    CommonModule,
    MaterialModule,
    RequirePermissionDirective,
    RouterModule,
    DisabledDirective,
  ],
  templateUrl: './layout-menu.component.html',
  styleUrls: ['./layout-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LayoutMenuComponent {
  protected readonly menuItemType = MenuItemType;
  protected readonly selectedItem$ = new Subject<MenuItem | null>();

  private readonly templates$ = this.templatesDomainService.templatesChange().pipe(
    map((templates) =>
      templates.map((t) => ({
        text: t.name,
        link: ['/dashboard', 'orders', 'overview', 'template', t.id.toString()],
      }))
    ),
    startWith([])
  );

  private readonly menuItems$ = this.templates$.pipe(
    map((subsections) =>
      LAYOUT_MENU_ITEMS.map((it) => (it.text === 'Órdenes' ? { ...it, subsections } : it))
    ),
    map((items) =>
      R.map(items, (item) =>
        R.addProp(item, 'url', R.join(Array.isArray(item.link) ? item.link : [item.link], '/'))
      )
    ),
    shareReplay(1),
    startWith(LAYOUT_MENU_ITEMS)
  );

  private readonly url$ = merge(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e.type === EventType.NavigationEnd),
      map((e) => e.urlAfterRedirects)
    ),
    defer(() => [this.router.url])
  );

  protected readonly activeItem$ = combineLatest([this.url$, this.menuItems$]).pipe(
    map(([url, items]) => R.find(items, (item) => url.includes((item as any).url))),
    tap((item) => this.selectedItem$.next(item ?? null)),
    takeUntilDestroyed(),
    shareReplay(1)
  );

  protected readonly expandedItem$ = merge(this.selectedItem$, this.activeItem$).pipe(
    distinctUntilChanged((a, b) => a?.text === b?.text),
    takeUntilDestroyed(),
    shareReplay(1)
  );

  protected readonly vm = Object.freeze({
    menuItems: toSignal(this.menuItems$),
    expandedItem: toSignal(this.expandedItem$),
    selectedItem: toSignal(this.selectedItem$),
    activeItem: toSignal(this.activeItem$),
    offlineIsActive: this.offlineDomainService.vm.activated,
  });

  constructor(
    private readonly templatesDomainService: TemplatesDomainService,
    private readonly cdr: ChangeDetectorRef,
    private readonly offlineDomainService: OfflineDomainService,
    private readonly router: Router
  ) {}
}
